import React, { useEffect, useRef, useState } from 'react';
import { Bot, User, PauseCircle, PlayCircle, Loader, ChevronDown } from 'lucide-react';
import {
  pauseThreadAgent,
  resumeThreadAgent,
  getThreadAgentState,
  type AgentConvKey,
  type ThreadDTO,
} from '../lib/api';

/**
 * Control humano (takeover) — prender/apagar el agente en UNA conversación.
 *
 * Universal y "viajero": se identifica la conversación por thread_id |
 * session_id | telefono (lo que la superficie tenga a mano) y todas apuntan al
 * mismo estado en el backend (ConversationThread.agent_paused_until), así el
 * toggle se refleja en cualquier lugar donde se muestre esa conversación.
 *
 * - Agente activo → botón "Tomar control" con menú (15/30/60 min / indefinido).
 * - En pausa → chip "Tú tienes el control · <tiempo>" + "Devolver al agente".
 *
 * Si no se pasan props de estado (`paused`), con `autoLoad` consulta el estado
 * por sí mismo (útil en superficies que no tienen el objeto thread en mano).
 */
type Props = {
  conv: AgentConvKey;
  paused?: boolean;
  pausedUntil?: string | null;
  indefinite?: boolean;
  autoLoad?: boolean;
  onChange?: (t: ThreadDTO) => void;
  className?: string;
};

const DURATIONS: { label: string; minutes: number | null }[] = [
  { label: '15 minutos', minutes: 15 },
  { label: '30 minutos', minutes: 30 },
  { label: '1 hora', minutes: 60 },
  { label: 'Indefinido', minutes: null },
];

function untilLabel(until?: string | null, indefinite?: boolean): string {
  if (indefinite || !until) return 'indefinido';
  const target = new Date(until).getTime();
  const mins = Math.round((target - Date.now()) / 60000);
  if (mins <= 0) return 'por expirar';
  if (mins < 60) return `${mins} min restantes`;
  return `hasta ${new Date(until).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`;
}

export function AgentControl({
  conv, paused, pausedUntil, indefinite, autoLoad, onChange, className,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [st, setSt] = useState<{ paused: boolean; until: string | null; indefinite: boolean }>({
    paused: !!paused, until: pausedUntil ?? null, indefinite: !!indefinite,
  });
  const rootRef = useRef<HTMLDivElement>(null);

  // Sincroniza cuando la superficie pasa estado fresco (p. ej. al recargar el thread).
  useEffect(() => {
    if (paused !== undefined) {
      setSt({ paused: !!paused, until: pausedUntil ?? null, indefinite: !!indefinite });
    }
  }, [paused, pausedUntil, indefinite]);

  // Carga inicial cuando la superficie no trae el estado.
  useEffect(() => {
    if (paused === undefined && autoLoad) {
      getThreadAgentState(conv)
        .then((t) => setSt({
          paused: !!t.agent_paused,
          until: t.agent_paused_until ?? null,
          indefinite: !!t.agent_paused_indefinite,
        }))
        .catch(() => { /* superficie sin hilo aún = agente activo (default) */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cerrar el menú al hacer click afuera.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const apply = async (fn: () => Promise<ThreadDTO>) => {
    if (busy) return;
    setBusy(true);
    setMenu(false);
    try {
      const t = await fn();
      setSt({
        paused: !!t.agent_paused,
        until: t.agent_paused_until ?? null,
        indefinite: !!t.agent_paused_indefinite,
      });
      onChange?.(t);
    } catch (e) {
      console.error('AgentControl action failed:', e);
      alert(`No se pudo cambiar el control del agente: ${(e as Error)?.message || 'error'}`);
    } finally {
      setBusy(false);
    }
  };

  if (st.paused) {
    return (
      <div className={`inline-flex items-center gap-2 ${className || ''}`}>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-200">
          <User className="h-3.5 w-3.5" />
          Tú tienes el control · {untilLabel(st.until, st.indefinite)}
        </span>
        <button
          onClick={() => apply(() => resumeThreadAgent(conv))}
          disabled={busy}
          title="Devolver el control al agente (vuelve a responder solo)"
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-emerald-500/40 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-700 dark:text-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {busy ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
          Devolver al agente
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`relative inline-flex items-center gap-2 ${className || ''}`}>
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-200">
        <Bot className="h-3.5 w-3.5" /> Agente activo
      </span>
      <button
        onClick={() => setMenu((m) => !m)}
        disabled={busy}
        title="Tomar el control (el agente deja de responder y te sugiere el borrador)"
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-amber-500/40 bg-amber-500/15 hover:bg-amber-500/25 text-amber-700 dark:text-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        {busy ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />}
        Tomar control
        <ChevronDown className="h-3 w-3" />
      </button>
      {menu && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[10rem] rounded-md border border-border bg-popover shadow-lg py-1">
          <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            Pausar el agente por
          </div>
          {DURATIONS.map((d) => (
            <button
              key={d.label}
              onClick={() => apply(() => pauseThreadAgent(conv, d.minutes))}
              className="block w-full text-left px-3 py-1.5 text-xs text-foreground hover:bg-accent transition"
            >
              {d.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
